import { toJpeg } from 'html-to-image';
import { QuizHtmlGenerator } from './QuizHtmlGenerator';

/**
 * Quiz Image Converter
 * 將題目 HTML 轉換為圖片（JPEG 格式）
 */
export class QuizImageConverter {
  constructor() {
    this.htmlGenerator = new QuizHtmlGenerator();
    this.tempContainer = null;
    this.cancelled = false; // 取消標記
  }

  /**
   * 取消轉換操作
   */
  cancel() {
    this.cancelled = true;
    console.log('🛑 QuizImageConverter 已取消');
  }

  /**
   * 將題目轉換為圖片
   * @param {Object} question - 題目對象
   * @param {Number} index - 題號
   * @returns {Promise<String>} Base64 圖片數據 (data:image/jpeg;base64,...)
   */
  async convertToImage(question, index) {
    try {
      // 1. 完全重建臨時容器（避免 MathJax 殘影）
      if (this.tempContainer && this.tempContainer.parentNode) {
        // 移除舊容器
        this.tempContainer.parentNode.removeChild(this.tempContainer);
        this.tempContainer = null;
      }

      // 創建全新容器
      this.tempContainer = document.createElement('div');
      this.tempContainer.id = `quiz-temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      this.tempContainer.style.position = 'fixed';
      this.tempContainer.style.left = '-10000px';
      this.tempContainer.style.top = '0';
      this.tempContainer.style.width = '800px';
      this.tempContainer.style.zIndex = '-1';
      document.body.appendChild(this.tempContainer);

      // 2. 生成 HTML 並插入容器
      const html = this.htmlGenerator.generateQuestionHtml(question, index);
      this.tempContainer.innerHTML = html;

      // 3. 找到實際的 Element 節點
      let targetElement = null;
      for (let i = 0; i < this.tempContainer.childNodes.length; i++) {
        const node = this.tempContainer.childNodes[i];
        if (node.nodeType === 1) {
          targetElement = node;
          break;
        }
      }

      if (!targetElement) {
        throw new Error('無法找到有效的 DOM Element');
      }

      // 4. 等待 DOM 渲染
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 200));

      // 5. 等待 MathJax 渲染完成
      await this.waitForMathJax();

      // 6. 等待所有非同步渲染完成
      await this.waitForAllRendering(targetElement);

      // 7. 額外等待確保完全渲染
      await new Promise(resolve => setTimeout(resolve, 500));

      // 8. 使用 html-to-image 轉換為 JPEG
      const dataUrl = await toJpeg(targetElement, {
        backgroundColor: '#ffffff',
        pixelRatio: 1.5,
        quality: 0.92
      });

      // 9. 驗證轉換結果
      if (!dataUrl || !dataUrl.startsWith('data:image/jpeg')) {
        throw new Error(`轉換結果格式錯誤: ${dataUrl ? dataUrl.substring(0, 50) : 'null'}`);
      }

      console.log(`✅ 題目 ${index} 轉換成功, 大小: ${Math.round(dataUrl.length / 1024)} KB`);

      // 下一題會完全重建容器，無需清理

      return dataUrl;
    } catch (error) {
      console.error(`題目 ${index} 轉換失敗:`, error);
      throw error;
    }
  }

  /**
   * 批次轉換多個題目
   * @param {Array} questions - 題目數組
   * @param {Function} onProgress - 進度回調 ({ current, total, percentage })
   * @returns {Promise<Array>} 圖片數據數組，失敗的為 null
   */
  async convertBatch(questions, onProgress) {
    const results = [];

    // ⚠️ 改為序列處理，避免共用容器導致的競爭條件
    for (let i = 0; i < questions.length; i++) {
      // 檢查是否已取消
      if (this.cancelled) {
        console.log(`🛑 轉換已取消，停止在第 ${i + 1}/${questions.length} 題`);
        break;
      }

      try {
        const dataUrl = await this.convertToImage(questions[i], i + 1);
        results.push(dataUrl);
      } catch (err) {
        console.error(`題目 ${i + 1} 轉換失敗:`, err);
        results.push(null); // 失敗時返回 null
      }

      // 回報進度
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: questions.length,
          percentage: Math.round(((i + 1) / questions.length) * 100)
        });
      }

      // 避免阻塞 UI（每 3 題讓出控制權）
      if ((i + 1) % 3 === 0) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }

    return results;
  }

  /**
   * 等待 MathJax 渲染完成
   */
  async waitForMathJax() {
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      // MathJax 未載入，直接返回
      console.warn('MathJax 未載入，跳過數學公式渲染');
      return;
    }

    try {
      // ✅ 清除舊的 MathJax 渲染（避免殘影）
      if (window.MathJax.typesetClear) {
        window.MathJax.typesetClear([this.tempContainer]);
        console.log('🧹 清除舊的 MathJax 渲染');
      }

      // 使用 MathJax 渲染當前容器
      await window.MathJax.typesetPromise([this.tempContainer]);
      console.log('✅ MathJax typesetPromise 完成');

      // 額外等待 600ms 確保 CHTML 完全渲染（增加等待時間）
      await new Promise(resolve => setTimeout(resolve, 600));
    } catch (error) {
      console.warn('MathJax 渲染警告:', error);
      // 即使 MathJax 渲染失敗，也繼續轉換
    }
  }

  /**
   * 等待所有非同步渲染完成
   * 檢查 MathJax 元素是否真正渲染完成
   */
  async waitForAllRendering(element) {
    const maxAttempts = 20;  // 最多檢查 20 次
    let attempts = 0;

    while (attempts < maxAttempts) {
      // 檢查是否有 MathJax 容器
      const mathContainers = element.querySelectorAll('mjx-container, .MJX-TEX, .mjx-chtml');

      if (mathContainers.length === 0) {
        // 沒有數學公式，直接返回
        console.log('✅ 無數學公式，跳過等待');
        return;
      }

      // 檢查所有數學容器是否有實際內容（非空）
      let allRendered = true;
      for (const container of mathContainers) {
        const hasContent = container.children.length > 0 ||
                          container.textContent.trim().length > 0 ||
                          container.querySelector('svg, mjx-math') !== null;

        if (!hasContent) {
          allRendered = false;
          break;
        }
      }

      if (allRendered) {
        console.log(`✅ 所有數學公式已渲染完成（檢查 ${attempts + 1} 次）`);

        // 額外等待確保布局穩定
        await new Promise(resolve => setTimeout(resolve, 300));
        return;
      }

      // 未完成，等待後重試
      attempts++;
      console.log(`⏳ 等待數學公式渲染... (${attempts}/${maxAttempts})`);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.warn('⚠️ 數學公式渲染超時，繼續截圖');
  }

  /**
   * 清理資源
   */
  destroy() {
    if (this.tempContainer && this.tempContainer.parentNode) {
      this.tempContainer.parentNode.removeChild(this.tempContainer);
      this.tempContainer = null;
    }
  }
}
